"use client";

import { useEffect, useState } from "react";
import { Loader2, Package, Pencil, Tag, Hash, Trash2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import ZoomableImage from "@/components/ui/zoomable-image";
import { getProductSKU } from "@/services/api/product-sku.service";
import { EditProductModal } from "@/components/chatbot/concept-visual-studio/_components/EditProductModal";
import ReusableAlertDialog from "@/components/shared/ReusableAlertDialog";
import { useDeleteProductSKU } from "@/hooks/useProductSKU";
import { toast } from "sonner";
import type {
  ProductSKUResponse,
  ProductAnalysis,
} from "@/types/product-sku.types";

interface ProductSKUDetailDialogProps {
  brandId: string;
  skuId: string | null;
  onClose: () => void;
}

export function ProductSKUDetailDialog({
  brandId,
  skuId,
  onClose,
}: ProductSKUDetailDialogProps) {
  const [sku, setSku] = useState<ProductSKUResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null
  );
  const [activeTab, setActiveTab] = useState<"images" | "analysis">("images");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refetchKey, setRefetchKey] = useState(0);
  const { mutate: deleteSKU, isPending: isDeleting } =
    useDeleteProductSKU(brandId);

  useEffect(() => {
    if (!skuId) return;
    setIsLoading(true);
    setSku(null);
    setSelectedVariantId(null);
    getProductSKU(brandId, skuId)
      .then((data) => {
        setSku(data);
        if (data.variants.length > 0) {
          setSelectedVariantId(data.variants[0].id);
        }
      })
      .catch(() => {
        setSku(null);
        onClose();
      })
      .finally(() => setIsLoading(false));
  }, [brandId, skuId, refetchKey]);

  const handleEditOpenChange = (open: boolean) => {
    setEditOpen(open);
    if (!open) setRefetchKey((k) => k + 1);
  };

  const selectedVariant =
    sku?.variants.find((v) => v.id === selectedVariantId) ?? null;

  useEffect(() => {
    setActiveTab("images");
  }, [selectedVariantId]);

  return (
    <>
      <Dialog open={!!skuId} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-none w-[1100px] h-[65vh] flex flex-col p-0 gap-0 overflow-hidden rounded-2xl">
          {isLoading ? (
            <div className="flex justify-center items-center flex-1">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
          ) : !sku ? (
            <div className="flex flex-col items-center justify-center flex-1 text-gray-400 gap-3">
              <Package className="h-10 w-10 opacity-30" />
              <p className="text-sm">Product not found.</p>
            </div>
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Header */}
              <div className="px-8 pt-6 pb-4 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {sku.name}
                  </h2>
                  <button
                    onClick={() => setEditOpen(true)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0"
                    aria-label="Edit product"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteOpen(true)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                    aria-label="Delete product"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-1.5">
                  {sku.sku_code && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Hash className="h-3 w-3" />
                      {sku.sku_code}
                    </span>
                  )}
                  {sku.category && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <Tag className="h-3 w-3" />
                      {sku.category}
                    </span>
                  )}
                  {sku.description && (
                    <span className="text-xs text-gray-400 truncate max-w-xs">
                      {sku.description}
                    </span>
                  )}
                </div>
              </div>

              {/* Body */}
              <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* Left — variant list */}
                <div className="w-72 shrink-0 border-r border-gray-100 overflow-y-auto bg-gray-50/40 py-3">
                  <p className="px-4 pb-2 font-semibold">Variants</p>
                  {sku.variants.length === 0 ? (
                    <p className="px-4 text-xs text-gray-400 italic">
                      No variants.
                    </p>
                  ) : (
                    sku.variants.map((variant, idx) => {
                      const thumb = variant.products[0]?.product_url ?? null;
                      const isActive = variant.id === selectedVariantId;
                      return (
                        <button
                          key={variant.id}
                          onClick={() => setSelectedVariantId(variant.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            isActive
                              ? "bg-indigo-50 border-r-2 border-indigo-500"
                              : "hover:bg-gray-100 border-r-2 border-transparent"
                          }`}
                        >
                          <div className="w-10 h-10 rounded-lg border border-gray-200 overflow-hidden shrink-0 bg-white">
                            {thumb ? (
                              <img
                                src={thumb}
                                alt={variant.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Package className="h-4 w-4 text-gray-300" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p
                              className={`text-sm font-medium truncate ${
                                isActive ? "text-indigo-700" : "text-gray-700"
                              }`}
                            >
                              {variant.name || `Variant ${idx + 1}`}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                              {variant.products.length} image
                              {variant.products.length !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Right — selected variant detail */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {!selectedVariant ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                      <Package className="h-8 w-8 opacity-30" />
                      <p className="text-sm">Select a variant.</p>
                    </div>
                  ) : (
                    <>
                      {/* Tab bar */}
                      <div className="relative flex items-center justify-center px-6 pt-4 pb-0 border-b border-gray-100 shrink-0">
                        <div className="flex items-center gap-1">
                          {(["images", "analysis"] as const).map((tab) => (
                            <button
                              key={tab}
                              onClick={() => setActiveTab(tab)}
                              className={`px-5 py-2 text-sm font-medium capitalize border-b-2 transition-colors -mb-px ${
                                activeTab === tab
                                  ? "border-indigo-500 text-indigo-600"
                                  : "border-transparent text-gray-500 hover:text-gray-700"
                              }`}
                            >
                              {tab}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tab content */}
                      <div className="flex-1 overflow-y-auto p-6">
                        {activeTab === "images" ? (
                          selectedVariant.products.length > 0 ? (
                            <div className="grid grid-cols-4 gap-3">
                              {selectedVariant.products.map((product, pIdx) => (
                                <div
                                  key={pIdx}
                                  className="flex flex-col rounded-xl border border-gray-200 overflow-hidden bg-gray-50"
                                >
                                  <div className="relative h-40 w-full overflow-hidden bg-gray-100">
                                    {product.product_url ? (
                                      <ZoomableImage
                                        src={product.product_url}
                                        alt={product.angle ?? "product"}
                                        className="absolute inset-0 h-full object-cover"
                                      />
                                    ) : (
                                      <div className="flex items-center justify-center h-full">
                                        <Package className="h-6 w-6 text-gray-300" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="px-2 py-1.5 text-xs font-medium text-gray-500 capitalize text-center border-t border-gray-100 bg-white h-8 flex items-center justify-center">
                                    {product.angle ?? ""}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
                              <Package className="h-8 w-8 opacity-30" />
                              <p className="text-sm italic">
                                No images for this variant.
                              </p>
                            </div>
                          )
                        ) : selectedVariant.analysis ? (
                          <AnalysisContent
                            analysis={selectedVariant.analysis}
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
                            <p className="text-sm italic">
                              No analysis available.
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {sku && (
        <EditProductModal
          open={editOpen}
          onOpenChange={handleEditOpenChange}
          sku={sku}
          brandId={brandId}
        />
      )}

      {sku && (
        <ReusableAlertDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete Product"
          description={
            <>
              Are you sure you want to permanently delete{" "}
              <span className="font-semibold text-gray-900">
                &quot;{sku.name}&quot;
              </span>
              ? This action cannot be undone.
            </>
          }
          confirmLabel="Delete"
          onConfirm={() =>
            deleteSKU(sku.id, {
              onSuccess: () => {
                toast.success("Product deleted");
                setDeleteOpen(false);
                onClose();
              },
              onError: (err) => toast.error(`Failed to delete: ${err.message}`),
            })
          }
          isLoading={isDeleting}
          danger
        />
      )}
    </>
  );
}

function AnalysisContent({ analysis }: { analysis: ProductAnalysis }) {
  return (
    <div className="space-y-5">
      {analysis.summary && (
        <p className="text-sm text-gray-600 leading-relaxed">
          {analysis.summary}
        </p>
      )}

      <div className="grid grid-cols-2 gap-5">
        {analysis.product_type && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Type
            </p>
            <p className="text-sm text-gray-800">{analysis.product_type}</p>
          </div>
        )}
        {analysis.hardware_finish && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Hardware Finish
            </p>
            <p className="text-sm text-gray-800">{analysis.hardware_finish}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-5">
        {[
          { label: "Colors", items: analysis.colors },
          { label: "Materials", items: analysis.materials },
          { label: "Style", items: analysis.style_keywords },
          { label: "Use Cases", items: analysis.use_cases },
          { label: "Best For", items: analysis.best_for },
        ]
          .filter((g) => g.items && g.items.length > 0)
          .map(({ label, items }) => (
            <div key={label} className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                {label}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {items!.map((item) => (
                  <Badge
                    key={item}
                    variant="secondary"
                    className="text-xs px-2.5 py-0.5 font-normal rounded-full"
                  >
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
